import { ChangeEvent, Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Checkbox, Icon } from '../../../components';
import { Icons } from '../../../components/Icon/Icon';
import styles from './WorkflowsTable.module.css';
import ToggleIcon from '../../../components/ToggleIcon/ToggleIcon';

// Input that shows a custom span tooltip on hover only when text is truncated (not when focused)
const TruncatedInput = (props: React.InputHTMLAttributes<HTMLInputElement>) => {
    const ref = useRef<HTMLInputElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const value = props.value ?? '';
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const updateTruncated = () => {
            setIsTruncated(el.scrollWidth > el.clientWidth);
        };
        updateTruncated();
        const ro = new ResizeObserver(updateTruncated);
        ro.observe(el);
        return () => ro.disconnect();
    }, [value]);
    const showTooltip = isTruncated && isHovered && !isFocused;
    const text = String(value ?? '');
    const { onFocus, onBlur, ...restProps } = props;
    return (
        <span
            className={styles.truncatedInputWrap}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <input
                ref={ref}
                {...restProps}
                onFocus={(e) => {
                    setIsFocused(true);
                    onFocus?.(e);
                }}
                onBlur={(e) => {
                    setIsFocused(false);
                    onBlur?.(e);
                }}
            />
            {showTooltip && text ? (
                <span className={styles.truncatedInputTooltip} role="tooltip">
                    {text}
                </span>
            ) : null}
        </span>
    );
};

interface WorkflowsTableProps {
    data?: Record<string, any>;
    onSelectionChange?: (dataWithSelection: Record<string, any>) => void;
    onRunTask?: (dataWithSelection: Record<string, any>) => void;
    selectedActions: string[];
    handleActionChange: (event: any) => void;
}

const formatLabel = (key: string): string => {
    // Sentence-case labels for keys:
    // - "ServiceProviders" -> "Service providers"
    // - "openEnded" -> "Open ended"
    // - "investmentFund" -> "Investment fund"
    // - "investment_fund" -> "Investment fund"
    if (key == 'laws') {
        return 'Relevant laws and regulations';
    } else if (key === 'licenses') {
        return 'License and approvals';
    } else if (key == 'Structure') {
        return 'Investment structure';
    } else if (key == 'AssetManager') {
        return 'Asset managers';
    }

    const words = key
        .replace(/_/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .split(' ')
        .filter(Boolean)
        .map((w) => w.toLowerCase());

    if (!words.length) return key;
    words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
    return words.join(' ');
};

// Icon per category (key); folder by default
const CATEGORY_ICONS: Record<string, Icons> = {
    structure: Icons.fund,
    generalpartner: Icons.fund,
    investors: Icons.investor,
    assets: Icons.asset,
    assetmanager: Icons.assetManagement,
    serviceproviders: Icons.serviceProvider,
};

const getCategoryIcon = (key: string): Icons =>
    key.toLowerCase() === 'other' || key.toLowerCase() === 'otherparttwo'
        ? Icons.folder
        : (CATEGORY_ICONS[key.replace(/\s+/g, '').toLowerCase()] ?? Icons.other);

// Count label per category (e.g. "X features"); default is key in lower case
const CATEGORY_COUNT_LABELS: Record<string, string> = {
    structure: 'features',
    generalpartner: 'features',
    other: 'blocks',
    otherparttwo: 'blocks',
    liquidityterms: 'terms',
    doubletaxtreaties: 'treaties',
    localtaxstatus: 'countries',
    distributioncountries: 'countries',
    laws: 'laws and regulations',
    licenses: 'licenses and approvals',
};

const getCategoryCountLabel = (key: string): string =>
    CATEGORY_COUNT_LABELS[key.replace(/\s+/g, '').toLowerCase()] ?? formatLabel(key).toLowerCase();

// Column type for TH (before label) / TD (after label in span). Values: T, B, L/T. Default T.
const COLUMN_TYPE_MAP: Record<string, string> = {
    // short: 'B',
    // long: 'L/T',
    // 'Investors|Domicile': 'B',
    // 'Investors|Investor type': 'T',
    // 'Assets|Domicile': 'B',
    // 'Assets|Asset type': 'T',
    // 'Assets|Allocation type': 'T',
    // 'AssetManager|Role': 'T',
    // 'AssetManager|Domicile / Name': 'T',
    // 'AssetManager|Domicile / Regulatory status': 'T',
    // 'ServiceProviders|Role': 'T',
    // 'ServiceProviders|Domicile / Name': 'T',
    // 'ServiceProviders|Domicile / Regulatory status': 'T',
    // 'distributionCountries|Domicile': 'B',
    // 'distributionCountries|Investors': 'T',
    // 'doubleTaxTreaties|Domicile': 'T',
    // 'laws|Domicile': 'B',
    // 'laws|Law': 'T',
    // 'licenses|Domicile': 'B',
    // 'licenses|License': 'T',
    // 'Feature title': '',
    // 'Feature value': '',
    domicile: 'L',
    law: 'L',
    'allocation type': 'L',
    'domicile / name': 'L/T',
    'domicile / regulatory status': 'L/T',
    evergreen: 'B',
    openEnded: 'B',
};

const getColumnType = (key: string, columnLabel?: string): string => {
    const mapKey =
        columnLabel === 'short' || columnLabel === 'long' ? columnLabel : columnLabel ? columnLabel.toLowerCase() : key;
    return COLUMN_TYPE_MAP[mapKey] ?? 'T';
};

// Check if array item has domicile/label (should not show nested children)
const hasDomicileOrLabel = (item: any): boolean => {
    if (typeof item !== 'object' || item === null) {
        return false;
    }

    // Check if it has domicile
    const hasDomicile =
        item.domicile &&
        ((typeof item.domicile === 'object' &&
            item.domicile !== null &&
            (item.domicile.label || item.domicile.domicileName || item.domicile.name)) ||
            typeof item.domicile === 'string');

    // Check if it has top-level label
    const hasLabel = item.label && typeof item.label === 'string';

    return hasDomicile || hasLabel;
};

// Extract label from array item: {domicile} {label}
const getArrayItemLabel = (item: any, index: number): string => {
    if (typeof item !== 'object' || item === null) {
        return `line ${index + 1}`;
    }

    const parts: string[] = [];

    // Extract domicile label
    if (item.domicile) {
        if (typeof item.domicile === 'object' && item.domicile !== null) {
            const domicileLabel = item.domicile.label || item.domicile.domicileName || item.domicile.name;
            if (domicileLabel) {
                parts.push(domicileLabel);
            }
        } else if (typeof item.domicile === 'string') {
            parts.push(item.domicile);
        }
    }

    // Extract top-level label
    if (item.label && typeof item.label === 'string') {
        parts.push(item.label);
    }

    // If we have parts, join them; otherwise fall back to line number
    return parts.length > 0 ? parts.join(' ') : `line ${index + 1}`;
};

interface TreeNode {
    key: string;
    label: string;
    value: any;
    path: string; // Full path like "Structure.name"
    children?: TreeNode[];
}

// Build tree structure recursively
const buildTree = (data: Record<string, any> | any, parentPath: string = ''): TreeNode[] => {
    const nodes: TreeNode[] = [];

    for (const key in data) {
        if (!data.hasOwnProperty(key)) continue;
        const value = data[key];
        const currentPath = parentPath ? `${parentPath}.${key}` : key;
        const node: TreeNode = {
            key: currentPath, // Use full path as key for uniqueness
            label: formatLabel(key),
            value,
            path: currentPath,
        };

        // If value is an array, create children for each array element
        if (Array.isArray(value)) {
            node.children = value.map((arrItem, arrIndex) => {
                const itemPath = `${currentPath}[${arrIndex}]`;
                const itemNode: TreeNode = {
                    key: itemPath,
                    label: getArrayItemLabel(arrItem, arrIndex),
                    value: arrItem,
                    path: itemPath,
                };

                // Only add children if the item doesn't have domicile/label
                // (items with domicile/label should be shown as single selectable items)
                if (!hasDomicileOrLabel(arrItem)) {
                    // If array item is an object, recursively add its children
                    if (typeof arrItem === 'object' && arrItem !== null && !Array.isArray(arrItem)) {
                        itemNode.children = buildTree(arrItem, itemPath);
                    } else if (Array.isArray(arrItem)) {
                        // Handle nested arrays - recursively process them
                        itemNode.children = arrItem.map((nestedItem, nestedIndex) => {
                            const nestedPath = `${itemPath}[${nestedIndex}]`;
                            const nestedNode: TreeNode = {
                                key: nestedPath,
                                label: getArrayItemLabel(nestedItem, nestedIndex),
                                value: nestedItem,
                                path: nestedPath,
                            };
                            // Only add children if nested item doesn't have domicile/label
                            if (!hasDomicileOrLabel(nestedItem)) {
                                // If nested item is an object, recursively add its children
                                if (
                                    typeof nestedItem === 'object' &&
                                    nestedItem !== null &&
                                    !Array.isArray(nestedItem)
                                ) {
                                    nestedNode.children = buildTree(nestedItem, nestedPath);
                                }
                            }
                            return nestedNode;
                        });
                    }
                }

                return itemNode;
            });
        }
        // If value is an object (not array, not null), recursively add children
        else if (typeof value === 'object' && value !== null) {
            node.children = buildTree(value, currentPath);
        }

        nodes.push(node);
    }

    return nodes;
};

const WorkflowsTable = ({
    data,
    onSelectionChange,
    onRunTask,
    selectedActions,
    handleActionChange,
}: WorkflowsTableProps) => {
    const [selection, setSelection] = useState<Record<string, any> | undefined>(undefined);
    const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

    const deepClone = (v: any) => JSON.parse(JSON.stringify(v));

    const getFromPath = (obj: any, path: string): any => {
        if (!obj) return undefined;
        const parts = path.split(/[\.\[\]]/).filter(Boolean);
        let cur = obj;
        for (const p of parts) {
            const key: any = /^\d+$/.test(p) ? parseInt(p, 10) : p;
            if (cur === null || cur === undefined) return undefined;
            // Return actual value (including null for unselected array items)
            cur = cur[key];
            if (cur === undefined) return undefined;
        }
        return cur; // Can be null (unselected) or actual value (selected)
    };

    const setInSelection = (sel: any, src: any, path: string, enabled: boolean) => {
        const parts = path.split(/[\.\[\]]/).filter(Boolean);
        const root = deepClone(sel || {});
        let curSel: any = root;
        let curSrc: any = src;

        for (let i = 0; i < parts.length; i++) {
            const isLast = i === parts.length - 1;
            const p = parts[i];
            const key: any = /^\d+$/.test(p) ? parseInt(p, 10) : p;
            const srcVal = curSrc?.[key];

            if (isLast) {
                if (!enabled) {
                    // Unselect: mark as null (preserves array indices)
                    if (Array.isArray(curSel)) {
                        if (key >= 0 && key < curSel.length) {
                            curSel[key] = null; // null = unselected, preserves index
                        }
                    } else if (curSel && typeof curSel === 'object') {
                        delete curSel[key];
                    }
                } else {
                    // Select: add/restore from source
                    if (srcVal === undefined) return root;
                    if (Array.isArray(curSel)) {
                        // Ensure array is long enough
                        while (curSel.length <= key) {
                            curSel.push(null);
                        }
                        curSel[key] = deepClone(srcVal);
                    } else {
                        if (curSel === undefined || curSel === null || typeof curSel !== 'object') {
                            curSel = {};
                        }
                        curSel[key] = deepClone(srcVal);
                    }
                }
            } else {
                // Navigate deeper
                if (curSel[key] === undefined || curSel[key] === null) {
                    curSel[key] = Array.isArray(srcVal) ? [] : {};
                }
                curSel = curSel[key];
                curSrc = srcVal;
            }
        }
        return root;
    };

    useEffect(() => {
        if (!data) {
            setSelection(undefined);
            return;
        }
        setSelection(deepClone(data)); // selection copy; original data rendered as-is
    }, [data]);

    const toggleExpand = (key: string) => {
        setExpandedKeys((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
    };

    const handleItemSelect = (path: string) => (e: any) => {
        if (!data) return;
        const next = setInSelection(selection, data, path, !!e?.target?.checked);
        setSelection(next);
        // Selection is only passed to parent on "Run task" click, not on every checkbox toggle
    };

    const handleFieldEdit = (path: string, fieldName: string) => (e: ChangeEvent<HTMLInputElement>) => {
        if (!selection || !data) return;
        const newValue = e.target.value;
        const fullPath = `${path}.${fieldName}`;

        // Helper function to update a nested property
        const updateNestedProperty = (obj: any, pathStr: string, value: any) => {
            const cloned = deepClone(obj);
            const parts = pathStr.split(/[\.\[\]]/).filter(Boolean);
            let current: any = cloned;

            for (let i = 0; i < parts.length - 1; i++) {
                const p = parts[i];
                const key: any = /^\d+$/.test(p) ? parseInt(p, 10) : p;
                if (current[key] === undefined || current[key] === null) {
                    current[key] = /^\d+$/.test(parts[i + 1]) ? [] : {};
                }
                current = current[key];
            }

            const lastKey = parts[parts.length - 1];
            const finalKey: any = /^\d+$/.test(lastKey) ? parseInt(lastKey, 10) : lastKey;
            current[finalKey] = value;

            return cloned;
        };

        // Update selection (cloned data)
        const updatedSelection = updateNestedProperty(selection, fullPath, newValue);
        setSelection(updatedSelection);

        // Update original data through parent callback
        if (onSelectionChange) {
            const updatedData = updateNestedProperty(data, fullPath, newValue);
            onSelectionChange(updatedData);
        }
    };

    const handleAddRow = (arrayPath: string) => () => {
        if (!data || !selection) return;

        // Hardcoded: Investors/Assets with domicile + nested array — append new domicile block to top-level array
        const investorsNestedMatch = arrayPath.match(/^Investors\[\d+\]\.investors$/);
        const assetsNestedMatch = arrayPath.match(/^Assets\[\d+\]\.asset$/);
        if (investorsNestedMatch || assetsNestedMatch) {
            const topKey = investorsNestedMatch ? 'Investors' : 'Assets';
            const nestedKey = investorsNestedMatch ? 'investors' : 'asset';
            const newBlock = {
                domicile: { label: '', value: '', labelShort: '' },
                [nestedKey]: [{}],
            };
            const updatedSelection = deepClone(selection);
            const updatedData = deepClone(data);
            if (!Array.isArray(updatedSelection[topKey])) updatedSelection[topKey] = [];
            if (!Array.isArray(updatedData[topKey])) updatedData[topKey] = [];
            updatedSelection[topKey].push(newBlock);
            updatedData[topKey].push(deepClone(newBlock));
            setSelection(updatedSelection);
            if (onSelectionChange) onSelectionChange(updatedData);
            return;
        }

        // Helper: add one empty object to array at path (for simple / one-level arrays)
        const addToArray = (obj: any, pathStr: string) => {
            const cloned = deepClone(obj);
            const parts = pathStr.split(/[\.\[\]]/).filter(Boolean);
            let current: any = cloned;

            for (let i = 0; i < parts.length - 1; i++) {
                const p = parts[i];
                const key: any = /^\d+$/.test(p) ? parseInt(p, 10) : p;
                if (current[key] === undefined || current[key] === null) {
                    current[key] = /^\d+$/.test(parts[i + 1]) ? [] : {};
                }
                current = current[key];
            }

            const lastPart = parts[parts.length - 1];
            const arrayKey: any = /^\d+$/.test(lastPart) ? parseInt(lastPart, 10) : lastPart;

            if (Array.isArray(current[arrayKey])) {
                current[arrayKey].push({});
            } else if (current[arrayKey] === undefined || current[arrayKey] === null) {
                current[arrayKey] = [{}];
            }

            return cloned;
        };

        const updatedSelection = addToArray(selection, arrayPath);
        setSelection(updatedSelection);

        if (onSelectionChange) {
            const updatedData = addToArray(data, arrayPath);
            onSelectionChange(updatedData);
        }
    };

    if (!data) {
        return <div className={styles.emptyState}>No data available</div>;
    }

    const thWithType = (sectionKey: string, label: string) => (
        <th>
            <span className={styles.columnType}>{getColumnType(sectionKey, label)}</span>
            {label}
        </th>
    );

    const renderTitles = (key: string) => {
        if (key === 'AssetManager' || key === 'ServiceProviders') {
            return (
                <>
                    <th></th>
                    <th></th>
                    {thWithType(key, 'Role')}
                    {thWithType(key, 'Domicile / Name')}
                    {thWithType(key, 'Domicile / Regulatory status')}
                </>
            );
        } else if (key === 'Investors') {
            return (
                <>
                    <th></th>
                    <th></th>
                    {thWithType(key, 'Domicile')}
                    {thWithType(key, 'Investor type')}
                </>
            );
        } else if (key === 'Assets') {
            return (
                <>
                    <th></th>
                    <th></th>
                    {thWithType(key, 'Domicile')}
                    {thWithType(key, 'Asset type')}
                    {thWithType(key, 'Allocation type')}
                </>
            );
        } else if (key === 'distributionCountries') {
            return (
                <>
                    <th></th>
                    <th></th>
                    {thWithType(key, 'Domicile')}
                    {thWithType(key, 'Investors')}
                </>
            );
        } else if (key === 'doubleTaxTreaties') {
            return (
                <>
                    <th></th>
                    <th></th>
                    {thWithType(key, 'Domicile')}
                    {thWithType(key, 'Domicile')}
                </>
            );
        } else if (key === 'laws') {
            return (
                <>
                    <th></th>
                    <th></th>
                    {thWithType(key, 'Domicile')}
                    {thWithType(key, 'Law')}
                </>
            );
        } else if (key === 'licenses') {
            return (
                <>
                    <th></th>
                    <th></th>
                    {thWithType(key, 'Domicile')}
                    {thWithType(key, 'License')}
                </>
            );
        } else {
            return (
                <>
                    <th></th>
                    <th></th>
                    <th>Feature title</th>
                    <th>Feature value</th>
                </>
            );
        }
    };

    const renderValues = (
        key: string,
        item: any,
        index: number,
        path: string,
        nestedItem?: any,
        nestedIndex?: number,
        isLastRow: boolean = false,
        arrayPath?: string,
    ) => {
        if (key === 'AssetManager' || key === 'ServiceProviders') {
            return (
                <>
                    <td>
                        <TruncatedInput type="text" value={item.role?.label} onChange={handleFieldEdit(path, 'name')} />
                        <input type="hidden" name={'role[value]'} value={item.role?.value} />
                    </td>
                    <td>
                        <TruncatedInput
                            type="text"
                            value={item.nameLabelLong}
                            onChange={handleFieldEdit(path, 'nameLabelLong')}
                        />
                        <input type="hidden" name={'domicile[value]'} value={item.domicile?.value} />
                        <input type="hidden" name={'role[value]'} value={item.domicile?.value} />
                    </td>
                    <td style={{ position: 'relative' }}>
                        <TruncatedInput
                            type="text"
                            value={item.regulatoryStatusLabelLong}
                            onChange={handleFieldEdit(path, 'regulatoryStatusLabelLong')}
                        />
                        <input type="hidden" name={'regulatoryStatus[value]'} value={item.regulatoryStatus?.value} />
                        <TruncatedInput
                            type="hidden"
                            name={'regulatoryStatus[domicileId]'}
                            value={item.regulatoryStatus?.domicileId}
                        />
                        {isLastRow && arrayPath && (
                            <button type="button" onClick={handleAddRow(arrayPath)} className={styles.addBtn}>
                                <Icon icon={Icons.plus} />
                            </button>
                        )}
                    </td>
                </>
            );
        } else if (key === 'Investors') {
            // Handle nested structure: item has domicile + investors array
            if (nestedItem) {
                // Rendering a nested investor item
                const parentPath = path.split('[')[0] + `[${index}]`;
                return (
                    <>
                        <td>
                            <TruncatedInput
                                type="text"
                                value={
                                    getFromPath(selection, `${parentPath}.domicile.label`) ??
                                    getFromPath(selection, `${parentPath}.domicile.domicileName`) ??
                                    getFromPath(selection, `${parentPath}.domicile.name`) ??
                                    (item.domicile?.label || item.domicile?.domicileName || item.domicile?.name || '')
                                }
                                onChange={handleFieldEdit(parentPath, 'domicile.label')}
                            />
                            <input type="hidden" name={'domicile[label]'} value={item.domicile?.label} />
                        </td>
                        <td style={{ position: 'relative' }}>
                            <TruncatedInput
                                type="text"
                                value={getFromPath(selection, `${path}.label`) ?? (nestedItem.label || '')}
                                onChange={handleFieldEdit(path, 'label')}
                            />
                            <input type="hidden" name={'value'} value={nestedItem.value} />
                            {isLastRow && arrayPath && (
                                <button type="button" onClick={handleAddRow(arrayPath)} className={styles.addBtn}>
                                    <Icon icon={Icons.plus} />
                                </button>
                            )}
                        </td>
                    </>
                );
            } else {
                // Fallback for old flat structure (backwards compatibility)
                return (
                    <>
                        <td>
                            <TruncatedInput
                                type="text"
                                value={item.domicile?.label}
                                onChange={handleFieldEdit(path, 'domicile.label')}
                            />
                            <input type="hidden" name={'country'} value={item.country} />
                        </td>
                        <td style={{ position: 'relative' }}>
                            <TruncatedInput
                                type="text"
                                value={item.label || ''}
                                onChange={handleFieldEdit(path, 'label')}
                            />
                            <input type="hidden" name={'value'} value={item.value} />
                            {isLastRow && arrayPath && (
                                <button type="button" onClick={handleAddRow(arrayPath)} className={styles.addBtn}>
                                    <Icon icon={Icons.plus} />
                                </button>
                            )}
                        </td>
                    </>
                );
            }
        } else if (key === 'Assets') {
            // Handle nested structure: item has domicile + asset array
            if (nestedItem) {
                // Rendering a nested asset item
                const parentPath = path.split('[')[0] + `[${index}]`;
                return (
                    <>
                        <td>
                            <TruncatedInput
                                type="text"
                                value={
                                    getFromPath(selection, `${parentPath}.domicile.label`) ??
                                    getFromPath(selection, `${parentPath}.domicile.domicileName`) ??
                                    getFromPath(selection, `${parentPath}.domicile.name`) ??
                                    (item.domicile?.label || item.domicile?.domicileName || item.domicile?.name || '')
                                }
                                onChange={handleFieldEdit(parentPath, 'domicile.label')}
                            />
                            <input type="hidden" name={'domicile[label]'} value={item.domicile?.label} />
                        </td>
                        <td>
                            <TruncatedInput
                                type="text"
                                value={getFromPath(selection, `${path}.label`) ?? (nestedItem.label || '')}
                                onChange={handleFieldEdit(path, 'label')}
                            />
                            <input type="hidden" name={'assetId'} value={nestedItem.assetId} />
                        </td>
                        <td style={{ position: 'relative' }}>
                            <TruncatedInput
                                type="text"
                                value={nestedItem.participationForm || ''}
                                onChange={handleFieldEdit(path, 'participationForm')}
                            />
                            <input type="hidden" name={'participationFormId'} value={nestedItem.participationFormId} />
                            {isLastRow && arrayPath && (
                                <button type="button" onClick={handleAddRow(arrayPath)} className={styles.addBtn}>
                                    <Icon icon={Icons.plus} />
                                </button>
                            )}
                        </td>
                    </>
                );
            } else {
                // Fallback for old flat structure (backwards compatibility)
                return (
                    <>
                        <td>
                            <TruncatedInput
                                type="text"
                                value={item.country || item.domicile?.label || ''}
                                onChange={handleFieldEdit(path, 'country')}
                            />
                            <input type="hidden" name={'country'} value={item.country} />
                        </td>
                        <td>
                            <TruncatedInput
                                type="text"
                                value={item.label || ''}
                                onChange={handleFieldEdit(path, 'label')}
                            />
                            <input type="hidden" name={'assetId'} value={item.assetId} />
                        </td>
                        <td style={{ position: 'relative' }}>
                            <TruncatedInput
                                type="text"
                                value={item.participationForm || ''}
                                onChange={handleFieldEdit(path, 'participationForm')}
                            />
                            <input type="hidden" name={'participationFormId]'} value={item.participationFormId} />
                            {isLastRow && arrayPath && (
                                <button type="button" onClick={handleAddRow(arrayPath)} className={styles.addBtn}>
                                    <Icon icon={Icons.plus} />
                                </button>
                            )}
                        </td>
                    </>
                );
            }
        } else if (key === 'distributionCountries') {
            const domicileLabel = item?.domicile?.label || item?.domicile?.domicileName || item?.domicile?.name || '';
            const investorsValue =
                typeof item?.investors === 'boolean' ? (item.investors ? 'Yes' : 'No') : String(item?.investors ?? '');
            return (
                <>
                    <td>
                        <TruncatedInput
                            type="text"
                            value={
                                getFromPath(selection, `${path}.domicile.label`) ??
                                getFromPath(selection, `${path}.domicile.domicileName`) ??
                                getFromPath(selection, `${path}.domicile.name`) ??
                                domicileLabel
                            }
                            onChange={handleFieldEdit(path, 'domicile.label')}
                        />
                    </td>
                    <td style={{ position: 'relative' }}>
                        <TruncatedInput
                            type="text"
                            value={getFromPath(selection, `${path}.investors`) ?? investorsValue}
                            onChange={handleFieldEdit(path, 'investors')}
                        />
                        {isLastRow && arrayPath && (
                            <button type="button" onClick={handleAddRow(arrayPath)} className={styles.addBtn}>
                                <Icon icon={Icons.plus} />
                            </button>
                        )}
                    </td>
                </>
            );
        } else if (key === 'doubleTaxTreaties') {
            const treatyLabelRaw = String(item?.label || item?.dttTitle || '');
            const [leftRaw, rightRaw] = treatyLabelRaw.split('<>');
            const domicileLeft = (leftRaw ?? '').trim();
            const domicileRight = (rightRaw ?? '').trim();
            return (
                <>
                    <td>
                        <TruncatedInput
                            type="text"
                            value={getFromPath(selection, `${path}.domicileLeft`) ?? domicileLeft}
                            onChange={handleFieldEdit(path, 'domicileLeft')}
                        />
                    </td>
                    <td style={{ position: 'relative' }}>
                        <TruncatedInput
                            type="text"
                            value={getFromPath(selection, `${path}.domicileRight`) ?? domicileRight}
                            onChange={handleFieldEdit(path, 'domicileRight')}
                        />
                        {isLastRow && arrayPath && (
                            <button type="button" onClick={handleAddRow(arrayPath)} className={styles.addBtn}>
                                <Icon icon={Icons.plus} />
                            </button>
                        )}
                    </td>
                </>
            );
        } else if (key === 'laws') {
            const domicileLabel = item?.domicile?.label || item?.domicile?.domicileName || item?.domicile?.name || '';
            const lawLabel = item?.label || item?.laws || '';
            return (
                <>
                    <td>
                        <TruncatedInput
                            type="text"
                            value={
                                getFromPath(selection, `${path}.domicile.label`) ??
                                getFromPath(selection, `${path}.domicile.domicileName`) ??
                                getFromPath(selection, `${path}.domicile.name`) ??
                                domicileLabel
                            }
                            onChange={handleFieldEdit(path, 'domicile.label')}
                        />
                    </td>
                    <td style={{ position: 'relative' }}>
                        <TruncatedInput
                            type="text"
                            value={
                                getFromPath(selection, `${path}.label`) ??
                                getFromPath(selection, `${path}.laws`) ??
                                lawLabel
                            }
                            onChange={handleFieldEdit(path, 'label')}
                        />
                        {isLastRow && arrayPath && (
                            <button type="button" onClick={handleAddRow(arrayPath)} className={styles.addBtn}>
                                <Icon icon={Icons.plus} />
                            </button>
                        )}
                    </td>
                </>
            );
        } else if (key === 'licenses') {
            const domicileLabel = item?.domicile?.label || item?.domicile?.domicileName || item?.domicile?.name || '';
            const licenseLabel = item?.label || item?.regulatoryStatus?.label || item?.name || '';
            return (
                <>
                    <td>
                        <TruncatedInput
                            type="text"
                            value={
                                getFromPath(selection, `${path}.domicile.label`) ??
                                getFromPath(selection, `${path}.domicile.domicileName`) ??
                                getFromPath(selection, `${path}.domicile.name`) ??
                                domicileLabel
                            }
                            onChange={handleFieldEdit(path, 'domicile.label')}
                        />
                    </td>
                    <td style={{ position: 'relative' }}>
                        <TruncatedInput
                            type="text"
                            value={
                                getFromPath(selection, `${path}.label`) ??
                                getFromPath(selection, `${path}.regulatoryStatus.label`) ??
                                getFromPath(selection, `${path}.name`) ??
                                licenseLabel
                            }
                            onChange={handleFieldEdit(path, 'label')}
                        />
                        {isLastRow && arrayPath && (
                            <button type="button" onClick={handleAddRow(arrayPath)} className={styles.addBtn}>
                                <Icon icon={Icons.plus} />
                            </button>
                        )}
                    </td>
                </>
            );
        } else if (key === 'localTaxStatus') {
            const domicileLabel = item?.domicile?.label || item?.domicile?.domicileName || item?.domicile?.name || '';
            const licenseLabel = item?.taxStatus?.label || '';
            return (
                <>
                    <td>
                        <TruncatedInput
                            type="text"
                            value={
                                getFromPath(selection, `${path}.domicile.label`) ??
                                getFromPath(selection, `${path}.domicile.domicileName`) ??
                                getFromPath(selection, `${path}.domicile.name`) ??
                                domicileLabel
                            }
                            onChange={handleFieldEdit(path, 'domicile.label')}
                        />
                    </td>
                    <td style={{ position: 'relative' }}>
                        <TruncatedInput
                            type="text"
                            value={getFromPath(selection, `${path}.taxStatus.label`) ?? licenseLabel}
                            onChange={handleFieldEdit(path, 'taxStatus.label')}
                        />
                        {isLastRow && arrayPath && (
                            <button type="button" onClick={handleAddRow(arrayPath)} className={styles.addBtn}>
                                <Icon icon={Icons.plus} />
                            </button>
                        )}
                    </td>
                </>
            );
            // } else if (item && typeof item === 'object' && !Array.isArray(item)) {
            //     return Object.keys(item).map((k) => {
            //         const val = getFromPath(selection, `${path}.${k}`) ?? item[k];
            //         const displayValue = typeof val === 'boolean' ? (val ? 'yes' : 'no') : String(val ?? '');
            //         return (
            //             <td key={k}>
            //                 <TruncatedInput type="text" value={displayValue} onChange={handleFieldEdit(path, k)} />
            //             </td>
            //         );
            //     });
        } else {
            // Generic fallback for arrays inside optional/nested objects (e.g. Other.* arrays)
            const fallbackLabel =
                (item && typeof item === 'object' && !Array.isArray(item) && (item.label || item.name)) ||
                item?.domicile?.label ||
                item?.domicile?.label ||
                item?.domicile?.domicileName ||
                item?.domicile?.name ||
                `line ${index + 1}`;
            const fallbackDetails =
                item && typeof item === 'object' && !Array.isArray(item) ? JSON.stringify(item) : String(item ?? '');
            return (
                <>
                    <td>
                        <TruncatedInput
                            type="text"
                            value={
                                getFromPath(selection, `${path}.label`) ??
                                getFromPath(selection, `${path}.name`) ??
                                getFromPath(selection, `${path}.domicile.label`) ??
                                getFromPath(selection, `${path}.domicile.domicileName`) ??
                                getFromPath(selection, `${path}.domicile.name`) ??
                                String(fallbackLabel)
                            }
                            onChange={handleFieldEdit(path, 'label')}
                        />
                    </td>
                    <td style={{ position: 'relative' }}>
                        <TruncatedInput
                            type="text"
                            value={getFromPath(selection, `${path}.details`) ?? fallbackDetails}
                            onChange={handleFieldEdit(path, 'details')}
                        />
                        {isLastRow && arrayPath && (
                            <button type="button" onClick={handleAddRow(arrayPath)} className={styles.addBtn}>
                                <Icon icon={Icons.plus} />
                            </button>
                        )}
                    </td>
                </>
            );
        }
    };

    const renderContent = (key: string, node: any, nodePath: string) => {
        // console.log({
        //     key,
        //     node,
        //     values: Object.values(node),
        // });
        if (Array.isArray(node)) {
            // Check if this is a nested structure (Investors or Assets with domicile + nested array)
            const isNestedStructure =
                (key === 'Investors' || key === 'Assets') &&
                node.length > 0 &&
                node[0] &&
                typeof node[0] === 'object' &&
                node[0].domicile &&
                (node[0].investors || node[0].asset);

            if (isNestedStructure) {
                // Flatten nested structure for rendering
                const nestedArrayKey = key === 'Investors' ? 'investors' : 'asset';
                let rowCounter = 0;

                // Calculate total rows
                let totalRows = 0;
                node.forEach((item) => {
                    const nestedArray = item[nestedArrayKey];
                    if (Array.isArray(nestedArray)) {
                        totalRows += nestedArray.length;
                    }
                });

                return (
                    <table className={`${styles.table}`}>
                        <thead>
                            <tr>{renderTitles(key)}</tr>
                        </thead>
                        <tbody>
                            {node.map((item, index) => {
                                const nestedArray = item[nestedArrayKey];
                                if (!Array.isArray(nestedArray)) return null;

                                return nestedArray.map((nestedItem: any, nestedIndex: number) => {
                                    rowCounter++;
                                    const itemPath = `${nodePath}[${index}].${nestedArrayKey}[${nestedIndex}]`;
                                    const isLastRow = rowCounter === totalRows;
                                    const arrayPath = `${nodePath}[${index}].${nestedArrayKey}`;
                                    return (
                                        <tr key={itemPath}>
                                            <td>
                                                <Checkbox
                                                    label={''}
                                                    isLabelFirst={true}
                                                    useMargin={false}
                                                    value={itemPath}
                                                    checked={(() => {
                                                        const val = getFromPath(selection, itemPath);
                                                        return val !== undefined && val !== null;
                                                    })()}
                                                    onChange={handleItemSelect(itemPath)}
                                                />
                                            </td>
                                            <td>{rowCounter}</td>
                                            {renderValues(
                                                key,
                                                item,
                                                index,
                                                itemPath,
                                                nestedItem,
                                                nestedIndex,
                                                isLastRow,
                                                arrayPath,
                                            )}
                                        </tr>
                                    );
                                });
                            })}
                        </tbody>
                    </table>
                );
            }

            // Regular flat array rendering
            return (
                <table
                    className={`${styles.table} ${key === 'Structure' || key === 'Liquidity terms' ? styles.tableVertical : ''}`}
                >
                    <thead>
                        <tr>{renderTitles(key)}</tr>
                    </thead>
                    <tbody>
                        {node.map((item, index) => {
                            const isLastRow = index === node.length - 1;
                            const arrayPath = nodePath;
                            return (
                                <tr key={`${key}[${index}]`}>
                                    <td>
                                        <Checkbox
                                            label={''}
                                            isLabelFirst={true}
                                            useMargin={false}
                                            value={`${nodePath}[${index}]`}
                                            checked={(() => {
                                                const val = getFromPath(selection, `${nodePath}[${index}]`);
                                                return val !== undefined && val !== null;
                                            })()}
                                            onChange={handleItemSelect(`${nodePath}[${index}]`)}
                                        />
                                    </td>
                                    <td>{index + 1}</td>

                                    {renderValues(
                                        key,
                                        item,
                                        index,
                                        `${nodePath}[${index}]`,
                                        undefined,
                                        undefined,
                                        isLastRow,
                                        arrayPath,
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            );
        }
        const arrayKeys = Object.keys(node).filter((k) => Array.isArray(node[k]));
        const isPlainKeyValueObject = (v: any) =>
            typeof v === 'object' && v !== null && !Array.isArray(v) && !v?.label && !v?.domicile;
        const plainObjectKeys = Object.keys(node).filter((k) => isPlainKeyValueObject(node[k]));
        const hasDirectRows = Object.keys(node).some((k) => !Array.isArray(node[k]) && !isPlainKeyValueObject(node[k]));

        return (
            <>
                {hasDirectRows && (
                    <table
                        className={`${styles.table} ${key === 'Structure' || key == 'Liquidity terms' ? styles.tableVertical : ''}`}
                    >
                        <thead>
                            <tr>{renderTitles(key)}</tr>
                        </thead>
                        <tbody>
                            {Object.keys(node).map((rowKey: any, index: number) => {
                                const value = node[rowKey];
                                const isPlainObject = isPlainKeyValueObject(value);
                                return (
                                    <Fragment key={`${rowKey}-${index}`}>
                                        {Array.isArray(value) ? (
                                            <></>
                                        ) : isPlainObject ? (
                                            <></>
                                        ) : rowKey === 'investmentFund' ||
                                          rowKey === 'fundRegime' ||
                                          rowKey === 'fundLegalForm' ? (
                                            <>
                                                {/* Render labelShort row */}
                                                {node[rowKey]?.labelShort && (
                                                    <tr key={`${rowKey}-${index}-short`}>
                                                        <td>
                                                            <Checkbox
                                                                label={''}
                                                                isLabelFirst={true}
                                                                useMargin={false}
                                                                value={`${nodePath}.${rowKey}`}
                                                                checked={
                                                                    getFromPath(selection, `${nodePath}.${rowKey}`) !==
                                                                    undefined
                                                                }
                                                                onChange={handleItemSelect(`${nodePath}.${rowKey}`)}
                                                            />
                                                        </td>
                                                        <td>{index + 1}</td>
                                                        <td>
                                                            {formatLabel(rowKey)} (short){' '}
                                                            <span className={styles.columnType}>
                                                                {getColumnType(rowKey, 'short')}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <TruncatedInput
                                                                type="text"
                                                                value={
                                                                    getFromPath(
                                                                        selection,
                                                                        `${nodePath}.${rowKey}.labelShort`,
                                                                    ) ?? node[rowKey].labelShort
                                                                }
                                                                onChange={handleFieldEdit(
                                                                    `${nodePath}.${rowKey}`,
                                                                    'labelShort',
                                                                )}
                                                            />
                                                        </td>
                                                    </tr>
                                                )}
                                                {/* Render labelLong row */}
                                                {node[rowKey]?.labelLong && (
                                                    <tr key={`${rowKey}-${index}-long`}>
                                                        <td>
                                                            <Checkbox
                                                                label={''}
                                                                isLabelFirst={true}
                                                                useMargin={false}
                                                                value={`${nodePath}.${rowKey}`}
                                                                checked={
                                                                    getFromPath(selection, `${nodePath}.${rowKey}`) !==
                                                                    undefined
                                                                }
                                                                onChange={handleItemSelect(`${nodePath}.${rowKey}`)}
                                                            />
                                                        </td>
                                                        <td>{index + 1}</td>
                                                        <td>
                                                            {formatLabel(rowKey)} (long){' '}
                                                            <span className={styles.columnType}>
                                                                {getColumnType(rowKey, 'long')}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <TruncatedInput
                                                                type="text"
                                                                value={
                                                                    getFromPath(
                                                                        selection,
                                                                        `${nodePath}.${rowKey}.labelLong`,
                                                                    ) ?? node[rowKey].labelLong
                                                                }
                                                                onChange={handleFieldEdit(
                                                                    `${nodePath}.${rowKey}`,
                                                                    'labelLong',
                                                                )}
                                                            />
                                                        </td>
                                                    </tr>
                                                )}
                                            </>
                                        ) : (
                                            <tr key={`${rowKey}-${index}-row`}>
                                                <td>
                                                    <Checkbox
                                                        label={''}
                                                        isLabelFirst={true}
                                                        useMargin={false}
                                                        value={`${nodePath}.${rowKey}`}
                                                        checked={
                                                            getFromPath(selection, `${nodePath}.${rowKey}`) !==
                                                            undefined
                                                        }
                                                        onChange={handleItemSelect(`${nodePath}.${rowKey}`)}
                                                    />
                                                </td>
                                                <td>{index + 1}</td>
                                                <td>
                                                    {formatLabel(rowKey)}{' '}
                                                    <span className={styles.columnType}>{getColumnType(rowKey)}</span>
                                                </td>
                                                <td>
                                                    <TruncatedInput
                                                        type="text"
                                                        value={
                                                            typeof node[rowKey] === 'boolean'
                                                                ? node[rowKey] == true
                                                                    ? 'yes'
                                                                    : 'no'
                                                                : typeof node[rowKey] === 'string'
                                                                  ? (getFromPath(selection, `${nodePath}.${rowKey}`) ??
                                                                    node[rowKey])
                                                                  : getFromPath(
                                                                        selection,
                                                                        `${nodePath}.${rowKey}.label`,
                                                                    ) ||
                                                                    node[rowKey]?.label ||
                                                                    ''
                                                        }
                                                        onChange={
                                                            typeof node[rowKey] === 'string'
                                                                ? handleFieldEdit(nodePath, rowKey)
                                                                : handleFieldEdit(`${nodePath}.${rowKey}`, 'label')
                                                        }
                                                    />
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                )}

                {key.toLowerCase() == 'other' &&
                    plainObjectKeys.map((k) => <Fragment key={k}>{renderNode(k, node[k], 0, nodePath)}</Fragment>)}
                {arrayKeys.map((key: any, index: number) => (
                    <Fragment key={`${key}-${index}`}>{renderNode(key, node[key], 0, nodePath)}</Fragment>
                ))}
            </>
        );
    };

    const renderNode = (key: string, node: any, level: number = 0, parentPath: string = '') => {
        const nodePath = parentPath ? `${parentPath}.${key}` : key;
        const isExpanded = expandedKeys.has(nodePath);
        // const hasChildren = node.children && node.children.length > 0;
        // const isSelected = selectedPaths.has(nodePath);

        return (
            <div key={nodePath} className={styles.treeNode}>
                <div className={styles.nodeRow} style={{ paddingLeft: `${level * 24}px` }}>
                    {/*{hasChildren ? (*/}
                    <button onClick={() => toggleExpand(nodePath)} type="button">
                        <span className={styles.nodeRowHeaderIcon}>
                            <ToggleIcon isActive={isExpanded} type={parentPath ? 'secondary' : 'primary'} />
                        </span>

                        <span className={styles.nodeRowHeaderIcon}>
                            <Icon icon={getCategoryIcon(key)} />
                        </span>

                        {/*{key}*/}
                        {/*) : (*/}
                        <span className={styles.expandPlaceholder}>
                            {formatLabel(key)}
                            <span className={styles.expandSubPlaceholder}>
                                {Array.isArray(node) ? node.length : Object.keys(node)?.length}{' '}
                                {getCategoryCountLabel(key)}
                            </span>
                        </span>
                        {/*)}*/}
                        {/*<div className={styles.checkboxWrapper}>*/}
                        {/*    <Checkbox*/}
                        {/*        checked={isSelected}*/}
                        {/*        onChange={(e) => togglePath(nodePath)}*/}
                        {/*        value={key}*/}
                        {/*        label={key}*/}
                        {/*        isLabelFirst={false}*/}
                        {/*    />*/}
                        {/*</div>*/}
                    </button>
                </div>
                {/*{hasChildren && isExpanded && <div>{node.children!.map((child) => renderNode(child, level + 1))}</div>}*/}
                {isExpanded && <>{renderContent(key, node, nodePath)}</>}
            </div>
        );
    };
    // console.log('data', data);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2 className={styles.title}>
                    <img src="/logo-small.svg" alt="" />
                    Extracted structure features:
                </h2>
            </div>
            <div className={styles.listContainerOuter}>
                <div className={styles.listContainer}>
                    {Object.keys(data).map((key: any) => renderNode(key, data[key]))}
                </div>
            </div>
            <div className={styles.footer}>
                {/*<span className={styles.selectedCount}>{selectedCount} selected</span>*/}
                <div className={styles.actions}>
                    <label className={styles.action}>
                        <input
                            type="checkbox"
                            name={'action[]'}
                            checked={selectedActions.includes('structure')}
                            value={'structure'}
                            onChange={handleActionChange}
                        />
                        <span>Save structure</span>
                    </label>
                    <label className={styles.action}>
                        <input
                            type="checkbox"
                            name={'action[]'}
                            checked={selectedActions.includes('tax_review')}
                            value={'tax_review'}
                            onChange={handleActionChange}
                        />
                        <span>Run tax review</span>
                    </label>
                    <label className={styles.action}>
                        <input
                            type="checkbox"
                            name={'action[]'}
                            checked={selectedActions.includes('compliance_review')}
                            value={'compliance_review'}
                            onChange={handleActionChange}
                        />
                        <span>Run compliance review</span>
                    </label>
                </div>

                <button
                    type="button"
                    onClick={() => {
                        if (onRunTask && selection) onRunTask(selection);
                    }}
                    className={styles.actionBtn}
                    disabled={!selectedActions.length}
                >
                    <Icon icon={Icons.forward} />
                </button>
            </div>
        </div>
    );
};

export default WorkflowsTable;
